import { Injectable, NgZone } from '@angular/core';
import { UpgradeModule, downgradeComponent, downgradeInjectable } from '@angular/upgrade/static';
import * as angular from 'angular';

import { ButtonComponent, IconComponent, BadgeComponent } from '@mode/capra';
import {
  BigValueRendererComponent,
  InPlaceInputComponent,
  InPlaceTextareaComponent,
  TableRendererComponent,
  VizRendererComponent,
} from '@mode/charts/ui';
import { ChartExportFacade } from '@mode/file-exports/domain';
import { ReportRunBannerComponent } from '@mode/report-viewer/ui';
import { ReportActionEmitService } from '@mode/reports/sandbox/domain-viewer';
import { FlamingoClientService } from '@mode/shared/data-access-flamingo';
import { FlatTableNgComponent } from '@mode/shared/ui';
import { CopyToClipboardDirective, ModalService, SvgIconComponent } from '@mode/shared/ui-angularjs';
import { CappieReporterService } from '@mode/shared/util-cappie-reporter';
import { HelpDocsService, MetaTagsService } from '@mode/shared/util-js';
import { BugsnagService } from '@mode/shared/util-error-handling';
import { AnalyticsService } from '@mode/shared/util-segment';
import { NotebookCellRendererComponent } from 'libs/notebooks/feature-notebooks/src';
import { ChartRenderer2Component } from '../../components/chart/chart-renderer2.component';
import { CopyLinkToVizDirective } from '../../components/copy-link-to-viz/copy-link-to-viz-directive';
import { ModeDatePickerDirective } from '../../components/date-picker/date-picker-directive';
import { dropdownAutoOrient } from '../../components/dropdown/dropdown-auto-orient-directive';
import { FilterModuleMultiComponent } from '../../components/filter-module-multi/filter-module-multi-component';
import { FilterModuleRangeComponent } from '../../components/filter-module-range/filter-module-range-component';
import { FilterModuleSingleComponent } from '../../components/filter-module-single/filter-module-single-component';
import { FilterPanelComponent } from '../../components/filter-panel/filter-panel-component';
import { InPlaceEditDirective } from '../../components/in-place-edit/in-place-edit-directive';
import { KebabMenuDirective } from '../../components/kebab-menu/kebab-menu';
import { MenuClipboardLinkDirective, MenuItemDirective } from '../../components/kebab-menu/menu-item';
import { modeEnterDirective } from '../../components/mode-enter/mode-enter-directive';
import { tableGridDirective } from '../../components/table/table-grid-directive';
import { TextBlockDirective } from '../../components/text-block/text-block-directive';
import { VizPlaceholderComponent } from '../../components/viz-placeholder/viz-placeholder.component';
import ColumnOptionsService from '../../services/column-options-service';
import FormatOptionsService from '../../services/format-options-service';
import { ReportApiService } from '../../services/report-api-service';
import ReportLinkService from '../../services/report-link-service';
import { TimingService } from '@mode/shared/util-js';
import { WindowMessageService } from '@mode/shared/util-iframe';
import { ChartInterpolaterService } from '../chart/domain/chart-interpolater-service';
import { ChartSanitizerService } from '../chart/domain/chart-sanitizer-service';
import ChartThemeService from '../chart/domain/chart-theme-service';
import { BigNumberComponent } from '../chart/ui/big-number/big-number-component';
import { LegacyChartDirective } from '../chart/ui/chart/chart-directive';
import { FillerbusterComponent } from '../chart/ui/fillerbuster/fillerbuster-component';
import { PivotTableComponent } from '../chart/ui/pivot-table/pivot-table-component';
import { DowngradedNgrxEffectsModule } from '../downgraded-angular/ngrx/effects.module';
import { DowngradedSharedUtilFeatureFlagsModule } from '../downgraded-angular/shared/util-feature-flags.module';
import { RunFailedErrorComponent } from '../embed-report-builder/components/run-failed-error.component';
import { EmbedViewerLayoutComponent } from './components/embed-viewer-layout.component';
import { EmbedViewerComponent } from './components/embed-viewer.component';
import { FilterPanelContainerComponent } from './components/filter-panel-container.component';
import { HeadlessViewerComponent } from './components/headless-viewer.component';
import { ModeChartComponent } from './components/mode-chart.component';
import { ModeNotebookCellComponent } from './components/mode-notebook.component';
import { ModeTableComponent } from './components/mode-table.component';
import { ModeTextComponent } from './components/mode-text.component';
import { VegasBigValueComponent } from './components/vegas-big-value.component';
import { VegasChartComponent as VegasChart2Component } from './components/vegas-chart.component';
import { VegasTableComponent } from './components/vegas-table.component';
import { ReportViewerFiltersContainerComponent } from './components/viewer-filters-container/viewer-filters-container.component';
import { EmbedLayoutFacade } from './domain/layout/embed-layout.facade';
import { LegacyEmbedViewerStoreService } from './domain/legacy-embed-viewer-store.service';
import { CustomJsService } from './services/custom-js.service';

const EMBED_REPORT_REQUIRES = [
  'asyncFilter',
  'ngSanitize',
  'ngCookies',
  'ui.bootstrap.dropdown',
  'ui.bootstrap.datepicker',
  'ui.bootstrap.tpls',
  'ui.bootstrap.tooltip',
  'ui.bootstrap.modal',
  DowngradedNgrxEffectsModule,
  DowngradedSharedUtilFeatureFlagsModule,
];

const angularModule = angular.module('embed.report', EMBED_REPORT_REQUIRES);

let initialized = false;

async function init() {
  initialized = true;

  const { Rpc, Emit } = await import(/* webpackChunkName: "iframe-transport" */ '@mode/reports/sandbox/util-transport');

  angularModule
    .component('modePivotTable', PivotTableComponent)
    .component('vegasChartToo', VegasChart2Component)
    .component('chartRendererToo', ChartRenderer2Component)
    .component('vegasTable', VegasTableComponent)
    .component('modeBigNumber', BigNumberComponent)
    .component('fillerbuster', FillerbusterComponent)
    .component('filterPanel', FilterPanelComponent)
    .component('filterModuleSingle', FilterModuleSingleComponent)
    .component('filterModuleMulti', FilterModuleMultiComponent)
    .component('filterModuleRange', FilterModuleRangeComponent)
    .component('filterPanelContainer', FilterPanelContainerComponent)
    .component('svgIcon', SvgIconComponent)
    .component('vizPlaceholder', VizPlaceholderComponent)
    .component('runFailedError', RunFailedErrorComponent)
    .directive('modeInPlaceInput', downgradeComponent({ component: InPlaceInputComponent }))
    .directive('modeInPlaceTextarea', downgradeComponent({ component: InPlaceTextareaComponent }))
    .directive('modeFlatTableNg', downgradeComponent({ component: FlatTableNgComponent }))
    .directive('modeTableRenderer', downgradeComponent({ component: TableRendererComponent }))
    .directive('modeVizRenderer', downgradeComponent({ component: VizRendererComponent }))
    .directive('capraIcon', downgradeComponent({ component: IconComponent }))
    .directive('capraBadge', downgradeComponent({ component: BadgeComponent }))
    .directive('capraButton', downgradeComponent({ component: ButtonComponent }))
    .directive('modeBigValueRenderer', downgradeComponent({ component: BigValueRendererComponent }))
    .directive('modeNotebookCellRenderer', downgradeComponent({ component: NotebookCellRendererComponent }))
    .directive('modeReportRunBanner', downgradeComponent({ component: ReportRunBannerComponent }))
    .directive('tableRenderer', tableGridDirective)
    .directive('inPlaceEdit', InPlaceEditDirective)
    .directive('textBlockRenderer', TextBlockDirective)
    .directive('modeMultiChart', LegacyChartDirective)
    .directive('modeDatePicker', ModeDatePickerDirective)
    .directive('dropdownAutoOrient', dropdownAutoOrient)
    .directive('modeEnter', modeEnterDirective)
    .service('AnalyticsService', downgradeInjectable(AnalyticsService))
    .service('ChartInterpolaterService', ChartInterpolaterService)
    .service('ChartSanitizerService', ChartSanitizerService)
    .service('ChartThemeService', ChartThemeService)
    .service('ColumnOptionsService', ColumnOptionsService)
    .service('FormatOptionsService', FormatOptionsService)
    .service('ReportApiService', ReportApiService)
    .service('EmbedReportActionEmitService', downgradeInjectable(ReportActionEmitService))
    .service('CappieReporterService', downgradeInjectable(CappieReporterService))
    .run(['CappieReporterService' /* force this service to load */, () => {}])
    .service('ReportLinkService', ReportLinkService)
    .service('TimingService', downgradeInjectable(TimingService))
    .service('ChartExportFacade', downgradeInjectable(ChartExportFacade))
    .service('WindowMessageService', WindowMessageService)
    .service('EmbedLayoutFacade', downgradeInjectable(EmbedLayoutFacade))
    .service('CustomJsService', downgradeInjectable(CustomJsService))
    .factory('NgZone', downgradeInjectable(NgZone))
    .service('ModalService', ModalService)
    .service('RxStoreService', downgradeInjectable(LegacyEmbedViewerStoreService))
    .constant('RpcTransport', Rpc)
    .constant('EmitTransport', Emit);

  return angularModule;
}

@Injectable({
  providedIn: 'root',
})
export class EmbedReportBootstrapService {
  constructor() {}

  async bootstrap(upgrade: UpgradeModule) {
    if (!initialized) {
      await init();
    }

    angularModule
      .component('embedLayout', EmbedViewerLayoutComponent)
      .component('embedViewer', EmbedViewerComponent)
      .component('headlessViewer', HeadlessViewerComponent)
      .component('modeChart', ModeChartComponent)
      .component('modePython', ModeNotebookCellComponent)
      .component('modeTable', ModeTableComponent)
      .component('modeText', ModeTextComponent)
      .component('vegasBigValue', VegasBigValueComponent)
      .directive(
        'modeReportViewerFiltersContainer',
        downgradeComponent({ component: ReportViewerFiltersContainerComponent })
      )
      .directive('kebabMenu', KebabMenuDirective)
      .directive('menuItem', MenuItemDirective)
      .directive('copyLinkToViz', CopyLinkToVizDirective)
      .directive('menuClipboardLink', MenuClipboardLinkDirective)
      .directive('copyToClipboard', CopyToClipboardDirective)
      .service('BugsnagService', downgradeInjectable(BugsnagService)) // only usable in headless-viewer
      .service('FlamingoClient', downgradeInjectable(FlamingoClientService)) // For headless-viewer
      .service('MetaTagsService', downgradeInjectable(MetaTagsService)) // For headless-viewer
      .service('HelpDocsService', downgradeInjectable(HelpDocsService));

    angularModule.config([
      '$compileProvider',
      function ($compileProvider) {
        $compileProvider.commentDirectivesEnabled(false);
        $compileProvider.cssClassDirectivesEnabled(false);
        if (process.env.NODE_ENV === 'production') {
          $compileProvider.debugInfoEnabled(false);
        }
      },
    ]);

    upgrade.bootstrap(document.body, [document.body.getAttribute('mode-app')], { strictDi: true });
  }
}

export { init };
